import { problemWorkApi } from './api/problem-work/problem-work-api'
import { configureStore } from '@reduxjs/toolkit'
import { setupListeners } from '@reduxjs/toolkit/query'
import { agentWritesApi } from './api/agents/agent-writes'
import { customAgentsApi } from './api/agents/custom-agents'
import { branchesApi } from './api/branches'
import { feedbackApi } from './api/feedback'
import { usageAnalyticsApi } from './api/usage-analytics'
import { gatewayLinkApi } from './api/gateway-link'
import { connectionHealthApi } from './api/connection-health'
import { chatArtifactsApi } from './api/chat-artifacts'
import { dashboardsApi, gwbkAutogenApi, templatesApi } from './api/dashboards'
import { flagsApi } from './api/flags'
import { docTemplatesApi, docsHubApi } from './api/docs-hub'
import { graphApi } from './api/graph'
import { ingestApi, plcSyncApi } from './api/ingest'
import { externalMessagingApi, integrationsApi } from './api/integrations'
import { modelPolicyApi } from './api/model-policy'
import { notificationRulesApi } from './api/notification-rules'
import { notificationsApi } from './api/notifications'
import { pssMirrorsApi } from './api/pss-mirrors'
import { resourceLabelsApi } from './api/resource-labels'
import { resourceAccessApi } from './api/resource-access/resource-access-api'
import { authzApi } from './api/authz/authz-api'
import { plantViewerApi } from './api/plant'
import { sightingsApi } from './api/sightings'
import { workOrdersApi } from './api/work-orders'
// Store-wiring bridges are intentionally not in the folder barrels (the
// branch-adoption bridge would otherwise create an import cycle through
// active-branch), so they keep their full module paths.
import { branchAdoptionBridge } from './api/branches/branch-adoption-bridge'
import { notificationRulesNotificationsBridge } from './api/notification-rules/notification-rules-notifications-bridge'
import { resourceLabelsBridge } from './api/resource-labels/resource-labels-bridge'
import { cacheCoherenceBridge } from './api/cache-coherence/cache-coherence-bridge'
import { queryInflightMiddleware, usageAnalyticsBridge } from './telemetry'
import {
  AuthenticationStorage,
  api as identityApi,
  authenticationSlice,
  authenticationLoginSuccessListener,
  authenticationLogoutListener,
  authenticationSetTokenListener,
  authenticationVerifyOnboardingListener
} from '@axilon-apps/domains-identity/state'
import agentOrchestratorReducer from './slices/agent-orchestrator-slice'
import customAgentConfigAgentReducer from './slices/custom-agent-config-agent-slice'
import graphReducer from './slices/graph-slice'
import ingestReducer from './slices/ingest-slice'
import ingestUploadProgressReducer from './slices/ingest-upload-progress-slice'
import sidebarTabsReducer from './slices/sidebar-tabs-slice'
import chatHandoffReducer from './slices/chat-handoff-slice'
import sessionPaintScopeReducer from './slices/session-paint-scope-slice'
import viewAsSlice from './slices/view-as-slice'
import flashMessageListener from './listeners/flash-message-listener'

export const initializeStore = (preloadedState = {}) =>
  configureStore({
    preloadedState,
    reducer: {
      agentOrchestrator: agentOrchestratorReducer,
      customAgentConfigAgent: customAgentConfigAgentReducer,
      graph: graphReducer,
      ingest: ingestReducer,
      ingestUploadProgress: ingestUploadProgressReducer,
      sidebarTabs: sidebarTabsReducer,
      chatHandoff: chatHandoffReducer,
      sessionPaintScope: sessionPaintScopeReducer,
      viewAs: viewAsSlice.reducer,
      [graphApi.reducerPath]: graphApi.reducer,
      [agentWritesApi.reducerPath]: agentWritesApi.reducer,
      [notificationsApi.reducerPath]: notificationsApi.reducer,
      [feedbackApi.reducerPath]: feedbackApi.reducer,
      [usageAnalyticsApi.reducerPath]: usageAnalyticsApi.reducer,
      [notificationRulesApi.reducerPath]: notificationRulesApi.reducer,
      [resourceLabelsApi.reducerPath]: resourceLabelsApi.reducer,
      [resourceAccessApi.reducerPath]: resourceAccessApi.reducer,
      [authzApi.reducerPath]: authzApi.reducer,
      [workOrdersApi.reducerPath]: workOrdersApi.reducer,
      [branchesApi.reducerPath]: branchesApi.reducer,
      [plantViewerApi.reducerPath]: plantViewerApi.reducer,
      [sightingsApi.reducerPath]: sightingsApi.reducer,
      [customAgentsApi.reducerPath]: customAgentsApi.reducer,
      [problemWorkApi.reducerPath]: problemWorkApi.reducer,
      [ingestApi.reducerPath]: ingestApi.reducer,
      [plcSyncApi.reducerPath]: plcSyncApi.reducer,
      [integrationsApi.reducerPath]: integrationsApi.reducer,
      [externalMessagingApi.reducerPath]: externalMessagingApi.reducer,
      [modelPolicyApi.reducerPath]: modelPolicyApi.reducer,
      [pssMirrorsApi.reducerPath]: pssMirrorsApi.reducer,
      [chatArtifactsApi.reducerPath]: chatArtifactsApi.reducer,
      [dashboardsApi.reducerPath]: dashboardsApi.reducer,
      [templatesApi.reducerPath]: templatesApi.reducer,
      [gwbkAutogenApi.reducerPath]: gwbkAutogenApi.reducer,
      [docTemplatesApi.reducerPath]: docTemplatesApi.reducer,
      [docsHubApi.reducerPath]: docsHubApi.reducer,
      [connectionHealthApi.reducerPath]: connectionHealthApi.reducer,
      [gatewayLinkApi.reducerPath]: gatewayLinkApi.reducer,
      [flagsApi.reducerPath]: flagsApi.reducer,
      [identityApi.reducerPath]: identityApi.reducer,
      [authenticationSlice.name]: authenticationSlice.reducer
    },
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware()
        .prepend(
          // Observes executeQuery lifecycle actions across every slice to feed
          // per-screen TTFD timing. Inert when no navigation is in
          // flight; no-op transport when perf telemetry is disabled.
          queryInflightMiddleware,
          usageAnalyticsBridge.middleware,
          notificationRulesNotificationsBridge.middleware,
          resourceLabelsBridge.middleware,
          branchAdoptionBridge.middleware,
          authenticationLoginSuccessListener.middleware,
          authenticationLogoutListener.middleware,
          authenticationSetTokenListener.middleware,
          authenticationVerifyOnboardingListener.middleware,
          flashMessageListener.middleware
        )
        .concat(
          graphApi.middleware,
          agentWritesApi.middleware,
          notificationsApi.middleware,
          feedbackApi.middleware,
          usageAnalyticsApi.middleware,
          notificationRulesApi.middleware,
          resourceLabelsApi.middleware,
          resourceAccessApi.middleware,
          authzApi.middleware,
          workOrdersApi.middleware,
          branchesApi.middleware,
          plantViewerApi.middleware,
          sightingsApi.middleware,
          customAgentsApi.middleware,
          problemWorkApi.middleware,
          ingestApi.middleware,
          plcSyncApi.middleware,
          integrationsApi.middleware,
          externalMessagingApi.middleware,
          modelPolicyApi.middleware,
          pssMirrorsApi.middleware,
          chatArtifactsApi.middleware,
          dashboardsApi.middleware,
          templatesApi.middleware,
          gwbkAutogenApi.middleware,
          docTemplatesApi.middleware,
          docsHubApi.middleware,
          connectionHealthApi.middleware,
          gatewayLinkApi.middleware,
          flagsApi.middleware,
          identityApi.middleware,
          // This listener stays after the RTK middleware so endpoint-local and
          // cross-slice invalidations are batched before refetching.
          cacheCoherenceBridge.middleware
        )
  })

// Hydrate the identity token from localStorage so a returning user starts
// authenticated. Guarded so importing the store in non-browser contexts
// (node-side jest) stays safe — AuthenticationStorage only touches
// localStorage inside initialize/save/clear.
const hasWindow = typeof window !== 'undefined'
if (hasWindow) {
  AuthenticationStorage.initialize()
}

const store = initializeStore(
  hasWindow ? { authentication: { token: AuthenticationStorage.get() } } : {}
)

// Wire RTK Query's window-level refetch listeners.
// Required so pages like the custom-agent config detail refresh their
// runs list when the user re-focuses the tab instead of waiting for the
// next polling tick.
setupListeners(store.dispatch)

export type AppStore = typeof store
export type RootState = ReturnType<AppStore['getState']>
export type AppDispatch = AppStore['dispatch']

export default store
